/**
 * Status pill (MASTER §6/§3). Color = semantic tone; never color-only — pairs an
 * icon with text. One canonical status→tone map reused everywhere.
 */
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock,
  Snowflake,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn.js';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
  info: 'bg-info/15 text-info',
  muted: 'bg-muted/15 text-muted',
  neutral: 'bg-surface-2 text-muted',
};

const TONE_ICON: Record<Tone, LucideIcon> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Snowflake,
  muted: CircleDashed,
  neutral: Clock,
};

/** Canonical status string → tone (MASTER §3). */
export function statusTone(status: string | null | undefined): Tone {
  switch (status) {
    case 'active':
    case 'paid':
    case 'completed':
    case 'issued':
    case 'attended':
    case 'received':
    case 'approved':
    case 'delivered':
    case 'sent':
      return 'success';
    case 'expiring_soon':
    case 'overdue':
    case 'low_stock':
    case 'failed_retriable':
    case 'partially_received':
    case 'late_cancel':
    case 'scheduled':
      return 'warning';
    case 'expired':
    case 'failed':
    case 'cancelled':
    case 'voided':
    case 'no_show':
    case 'rejected':
      return 'danger';
    case 'frozen':
    case 'requested':
    case 'waiting':
    case 'promoted':
      return 'info';
    case 'pending':
    case 'draft':
    case 'not_required':
      return 'muted';
    default:
      return 'neutral';
  }
}

export function StatusPill({
  status,
  label,
  tone,
  className,
}: {
  status?: string | null;
  label: ReactNode;
  tone?: Tone;
  className?: string;
}): ReactNode {
  const resolved = tone ?? statusTone(status);
  const Icon = TONE_ICON[resolved];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
        TONE_CLASS[resolved],
        className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

/** Plain colored chip without an icon (counts, tags). */
export function Chip({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
